import React, {useMemo} from 'react';
import {NumberInput, Select} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconInfoCircle} from '@tabler/icons-react';
import dayjs from 'dayjs';
import type {DatePreset} from 'src/design/atoms/datePresets';
import {DatePickerWithPresets} from 'src/design/atoms/DatePickerWithPresets';
import type {
  WorkflowDefinitionTriggerResponse} from 'src/api/ProactApiClient';
import {
  DayOfWeek,
  IntervalTypeEnum
} from 'src/api/ProactApiClient';
import {SelectableCard} from 'src/design/molecules/SelectableCard';
import type {TriggerEditorProps} from './TriggerCard';
import {SectionLabel} from './SectionLabel';

const intervalConfigs = [
  {
    type: IntervalTypeEnum.Daily,
    label: 'Daily',
    description: 'Run every day at the specified hour.',
  },
  {
    type: IntervalTypeEnum.Weekly,
    label: 'Weekly',
    description: 'Run once per week on a specified day and hour.',
  },
  {
    type: IntervalTypeEnum.Monthly,
    label: 'Monthly',
    description: 'Run once per month on a specified day and hour.',
  },
  {
    type: IntervalTypeEnum.Frequency,
    label: 'Custom Frequency',
    description: 'Run at a custom interval (every N days) from the start date.',
  },
];

const formatUtcToLocal = (
  utcHour: number
): {display: string; military: string} => {
  const now = new Date();
  const utcDate = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      utcHour,
      0,
      0
    )
  );
  const display = utcDate.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
  const military = utcDate.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return {display, military};
};

const getLocalTimezoneAbbreviation = (): string => {
  const formatter = new Intl.DateTimeFormat(undefined, {
    timeZoneName: 'short',
  });
  const parts = formatter.formatToParts(new Date());
  const tzPart = parts.find(p => p.type === 'timeZoneName');
  return tzPart?.value ?? 'Local';
};

const calculateNextScheduledRun = (
  trigger: WorkflowDefinitionTriggerResponse
): Date | null => {
  if (!trigger.scheduleStartDate || trigger.scheduleHour == null) {
    return null;
  }

  const now = new Date();
  const startDate = new Date(trigger.scheduleStartDate);
  const hour = trigger.scheduleHour;

  let nextRun: Date;

  switch (trigger.scheduleIntervalType) {
    case IntervalTypeEnum.Daily: {
      const todayAtHour = new Date(
        Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
          now.getUTCDate(),
          hour
        )
      );
      nextRun =
        todayAtHour > now
          ? todayAtHour
          : new Date(
              Date.UTC(
                now.getUTCFullYear(),
                now.getUTCMonth(),
                now.getUTCDate() + 1,
                hour
              )
            );
      break;
    }
    case IntervalTypeEnum.Weekly: {
      if (trigger.scheduleDayOfWeek == null) return null;
      const targetDay = trigger.scheduleDayOfWeek;
      const currentDay = now.getUTCDay();
      let daysUntilTarget = (targetDay - currentDay + 7) % 7;
      if (
        daysUntilTarget === 0 &&
        (now.getUTCHours() > hour ||
          (now.getUTCHours() === hour && now.getUTCMinutes() > 0))
      ) {
        daysUntilTarget = 7;
      }
      nextRun = new Date(
        Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
          now.getUTCDate() + daysUntilTarget,
          hour
        )
      );
      break;
    }
    case IntervalTypeEnum.Monthly: {
      if (trigger.scheduleDayOfMonth == null) return null;
      const targetDayOfMonth = trigger.scheduleDayOfMonth;
      let year = now.getUTCFullYear();
      let month = now.getUTCMonth();
      if (
        now.getUTCDate() > targetDayOfMonth ||
        (now.getUTCDate() === targetDayOfMonth &&
          (now.getUTCHours() > hour ||
            (now.getUTCHours() === hour && now.getUTCMinutes() > 0)))
      ) {
        month++;
        if (month > 11) {
          month = 0;
          year++;
        }
      }
      nextRun = new Date(Date.UTC(year, month, targetDayOfMonth, hour));
      break;
    }
    case IntervalTypeEnum.Frequency: {
      if (trigger.scheduleIntervalDays == null) return null;
      const intervalDays = trigger.scheduleIntervalDays;
      let candidate = new Date(
        Date.UTC(
          startDate.getUTCFullYear(),
          startDate.getUTCMonth(),
          startDate.getUTCDate(),
          hour
        )
      );
      while (candidate <= now) {
        candidate = new Date(
          candidate.getTime() + intervalDays * 24 * 60 * 60 * 1000
        );
      }
      nextRun = candidate;
      break;
    }
    default:
      return null;
  }

  if (startDate > nextRun) {
    nextRun = new Date(
      Date.UTC(
        startDate.getUTCFullYear(),
        startDate.getUTCMonth(),
        startDate.getUTCDate(),
        hour
      )
    );
  }

  if (trigger.scheduleEndDate) {
    const endDate = new Date(trigger.scheduleEndDate);
    const endOfDay = new Date(
      Date.UTC(
        endDate.getUTCFullYear(),
        endDate.getUTCMonth(),
        endDate.getUTCDate(),
        23,
        59,
        59
      )
    );
    if (nextRun > endOfDay) {
      return null;
    }
  }

  return nextRun;
};

function generateSequentialNumbers(start: number, end: number): number[] {
  const numbers: number[] = [];
  for (let i = start; i <= end; i++) {
    numbers.push(i);
  }
  return numbers;
}

const hourOptions = generateSequentialNumbers(0, 23).map(hour => ({
  value: String(hour),
  label: `${String(hour).padStart(2, '0')}:00`,
}));

const dayNumberOptions = generateSequentialNumbers(1, 28).map(day => ({
  value: String(day),
  label: String(day),
}));

const dayNameOptions = [
  {value: String(DayOfWeek.Sunday), label: 'Sunday'},
  {value: String(DayOfWeek.Monday), label: 'Monday'},
  {value: String(DayOfWeek.Tuesday), label: 'Tuesday'},
  {value: String(DayOfWeek.Wednesday), label: 'Wednesday'},
  {value: String(DayOfWeek.Thursday), label: 'Thursday'},
  {value: String(DayOfWeek.Friday), label: 'Friday'},
  {value: String(DayOfWeek.Saturday), label: 'Saturday'},
];

export const TriggerScheduleEditor: React.FC<TriggerEditorProps> = ({
  trigger,
  onTriggerChange,
}) => {
  const schedulePresets = useMemo<DatePreset[]>(
    () => [
      {value: dayjs().format('YYYY-MM-DD'), label: 'Today'},
      {value: dayjs().add(1, 'week').format('YYYY-MM-DD'), label: 'Next week'},
      {value: dayjs().add(1, 'month').format('YYYY-MM-DD'), label: 'Next month'},
      {value: dayjs().add(1, 'year').format('YYYY-MM-DD'), label: 'Next year'},
    ],
    []
  );

  const localTimezone = useMemo(() => getLocalTimezoneAbbreviation(), []);

  const localTime = useMemo(() => {
    if (trigger.scheduleHour == null) return null;
    return formatUtcToLocal(trigger.scheduleHour);
  }, [trigger.scheduleHour]);

  const nextScheduledRun = useMemo(
    () => calculateNextScheduledRun(trigger),
    [trigger]
  );

  const handleIntervalTypeChange = (value: string) => {
    onTriggerChange({
      ...trigger,
      scheduleIntervalType: Number(value) as IntervalTypeEnum,
    });
  };

  const handleStartDateChange = (value: Date | null) => {
    if (value) {
      onTriggerChange({...trigger, scheduleStartDate: value});
    }
  };

  const handleEndDateChange = (value: Date | null) => {
    if (value) {
      onTriggerChange({...trigger, scheduleEndDate: value});
    }
  };

  const handleHourChange = (value: string | null) => {
    if (value !== null) {
      onTriggerChange({...trigger, scheduleHour: Number(value)});
    }
  };

  const handleDayOfWeekChange = (value: string | null) => {
    if (value !== null) {
      onTriggerChange({
        ...trigger,
        scheduleDayOfWeek: Number(value) as DayOfWeek,
      });
    }
  };

  const handleDayOfMonthChange = (value: string | null) => {
    if (value !== null) {
      onTriggerChange({...trigger, scheduleDayOfMonth: Number(value)});
    }
  };

  const handleIntervalDaysChange = (value: string | number) => {
    onTriggerChange({
      ...trigger,
      scheduleIntervalDays: Number(value) || undefined,
    });
  };

  const startDate = trigger.scheduleStartDate
    ? new Date(trigger.scheduleStartDate)
    : null;
  const endDate = trigger.scheduleEndDate
    ? new Date(trigger.scheduleEndDate)
    : null;

  return (
    <Stack gap="md">
      <SectionLabel label="Frequency" withSeparation={false} />
      <SimpleGrid cols={{base: 1, sm: 2}}>
        {intervalConfigs.map(c => (
          <SelectableCard
            key={c.type}
            label={c.label}
            description={c.description}
            value={String(c.type)}
            selected={trigger.scheduleIntervalType === c.type}
            onClick={handleIntervalTypeChange}
          />
        ))}
      </SimpleGrid>

      <SectionLabel label="Timing" />

      <DatePickerWithPresets
        presets={schedulePresets}
        label="Start Date"
        description="The schedule will begin running on or after this date."
        value={startDate}
        minDate={new Date()}
        maxDate={endDate ?? undefined}
        onChange={handleStartDateChange}
        required
      />

      <DatePickerWithPresets
        presets={schedulePresets}
        label="End Date"
        description="The schedule will stop running after this date."
        value={endDate}
        minDate={startDate ?? undefined}
        maxDate={new Date('2037-12-31')}
        onChange={handleEndDateChange}
        required
      />

      <Select
        label="Run at hour (UTC)"
        description={
          localTime
            ? `This is ${localTime.display} (${localTime.military}) in your local timezone (${localTimezone}).`
            : undefined
        }
        data={hourOptions}
        value={trigger.scheduleHour != null ? String(trigger.scheduleHour) : null}
        onChange={handleHourChange}
        required
      />

      {trigger.scheduleIntervalType === IntervalTypeEnum.Weekly && (
        <Select
          label="Day of week"
          description="The workflow will run every week on this day."
          data={dayNameOptions}
          value={
            trigger.scheduleDayOfWeek != null
              ? String(trigger.scheduleDayOfWeek)
              : null
          }
          onChange={handleDayOfWeekChange}
        />
      )}

      {trigger.scheduleIntervalType === IntervalTypeEnum.Monthly && (
        <Select
          label="Day of month"
          description="The workflow will run every month on this day. Limited to 1-28 to ensure the day exists in all months."
          data={dayNumberOptions}
          value={
            trigger.scheduleDayOfMonth != null
              ? String(trigger.scheduleDayOfMonth)
              : null
          }
          onChange={handleDayOfMonthChange}
        />
      )}

      {trigger.scheduleIntervalType === IntervalTypeEnum.Frequency && (
        <NumberInput
          label="Run every N days"
          description="The number of days between each run. Must be between 1 and 2550."
          value={trigger.scheduleIntervalDays ?? 30}
          onChange={handleIntervalDaysChange}
          min={1}
          max={2550}
        />
      )}

      {nextScheduledRun && (
        <Alert
          icon={<IconInfoCircle size={16} />}
          title="Schedule Preview"
          color="blue"
          variant="light">
          <Text size="sm">
            <strong>Next scheduled run:</strong>
          </Text>
          <Text size="sm">
            {nextScheduledRun.toUTCString().replace('GMT', 'UTC')}
          </Text>
          <Text size="sm" c="dimmed">
            ({nextScheduledRun.toLocaleString()} {localTimezone})
          </Text>
        </Alert>
      )}
    </Stack>
  );
};
